const DEFAULT_OPTIONS = {
    fieldSelector: 'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), select, textarea',
    fieldWrapperSelector: '[data-validate-field], .form-group, .custom-field, .input-group',
    errorClass: 'has-error',
    errorMessageClass: 'form-validation',
    errorMessage: 'Campo obrigatorio',
    invalidMessageTemplate: 'Informe {field}.',
    selectMessageTemplate: 'Selecione {field}.',
    focusInvalid: true
};

const TRUE_VALUES = ['1', 'true', 'required', 'yes'];
const FALSE_VALUES = ['0', 'false', 'optional', 'no'];

const mergeOptions = (options = {}) => ({
    ...DEFAULT_OPTIONS,
    ...options
});

const toBoolean = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value !== 'string') {
        return false;
    }

    return TRUE_VALUES.includes(value.toLowerCase());
};

const isFalseValue = (value) => typeof value === 'string' && FALSE_VALUES.includes(value.toLowerCase());

const normalizeText = (value = '') => value
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const escapeAttributeValue = (value = '') => value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');

const isElementVisible = (element) => {
    if (!element || element.type === 'hidden' || element.disabled) {
        return false;
    }

    if (element.closest('[hidden], [data-validate-ignore]')) {
        return false;
    }

    let current = element;

    while (current && current !== document.documentElement) {
        const style = window.getComputedStyle(current);

        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }

        current = current.parentElement;
    }

    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
};

const getFieldWrapper = (field, options) => (
    field.closest(options.fieldWrapperSelector) || field.parentElement
);

const getFieldLabel = (field, wrapper) => {
    if (field.dataset.validateLabel) {
        return field.dataset.validateLabel;
    }

    if (field.id) {
        const label = document.querySelector(`label[for="${field.id}"]`);

        if (label) {
            return label.textContent;
        }
    }

    const wrapperLabel = wrapper ? wrapper.querySelector('label') : null;

    if (wrapperLabel) {
        return wrapperLabel.textContent;
    }

    return field.getAttribute('aria-label') || field.placeholder || field.name || 'este campo';
};

const hasRequiredMarker = (text = '') => {
    const normalized = text.toLowerCase();

    if (normalized.includes('opcional') || normalized.includes('optional')) {
        return false;
    }

    return text.includes('*');
};

const isFieldRequired = (field, options) => {
    const wrapper = getFieldWrapper(field, options);

    if (isFalseValue(field.dataset.validateRequired)) {
        return false;
    }

    if (field.required || toBoolean(field.dataset.validateRequired) || field.getAttribute('aria-required') === 'true') {
        return true;
    }

    if (wrapper && (wrapper.classList.contains('required') || toBoolean(wrapper.dataset.validateRequired))) {
        return true;
    }

    return hasRequiredMarker(getFieldLabel(field, wrapper)) || hasRequiredMarker(field.getAttribute('aria-label') || '');
};

const getGroupFields = (form, field) => {
    if (!field.name || (field.type !== 'radio' && field.type !== 'checkbox')) {
        return [field];
    }

    return Array.from(form.querySelectorAll(`[name="${escapeAttributeValue(field.name)}"]`));
};

const getFieldValue = (field, form) => {
    if (field.type === 'radio' || field.type === 'checkbox') {
        return getGroupFields(form, field).some((item) => item.checked) ? 'checked' : '';
    }

    if (field.tagName === 'SELECT') {
        return field.value;
    }

    return (field.value || '').trim();
};

const hasFieldValue = (field, form) => {
    const value = getFieldValue(field, form);
    const emptyValues = (field.dataset.validateEmptyValues || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    return Boolean(value) && !emptyValues.includes(value);
};

const getRequiredMessage = (field, options, wrapper) => {
    if (field.dataset.validateMessage) {
        return field.dataset.validateMessage;
    }

    if (wrapper && wrapper.dataset.validateMessage) {
        return wrapper.dataset.validateMessage;
    }

    const fieldLabel = normalizeText(getFieldLabel(field, wrapper));
    const fallbackLabel = fieldLabel || options.errorMessage;
    const template = field.tagName === 'SELECT' || field.type === 'radio' || field.type === 'checkbox'
        ? options.selectMessageTemplate
        : options.invalidMessageTemplate;

    return template.replace('{field}', fallbackLabel);
};

const ensureErrorId = (field) => {
    if (field.id) {
        return `error-${field.id.replace(/^input-/, '').replace(/_/g, '-')}`;
    }

    return `error-${field.name.replace(/[\[\]_]+/g, '-')}`;
};

const getExistingError = (wrapper, options) => wrapper
    ? wrapper.querySelector(`.${options.errorMessageClass}[data-form-validation-error="true"]`)
    : null;

const getAnyError = (wrapper, options) => wrapper
    ? wrapper.querySelector(`.${options.errorMessageClass}`)
    : null;

const syncAriaDescribedBy = (field, errorId, shouldAdd) => {
    const values = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    const hasErrorId = values.includes(errorId);

    if (shouldAdd && !hasErrorId) {
        values.push(errorId);
    }

    if (!shouldAdd && hasErrorId) {
        values.splice(values.indexOf(errorId), 1);
    }

    if (values.length) {
        field.setAttribute('aria-describedby', values.join(' '));
    } else {
        field.removeAttribute('aria-describedby');
    }
};

export const clearFieldValidation = (field, options = {}) => {
    const config = mergeOptions(options);
    const wrapper = getFieldWrapper(field, config);
    const error = getExistingError(wrapper, config);
    const fields = field.form ? getGroupFields(field.form, field) : [field];

    if (wrapper) {
        wrapper.classList.remove(config.errorClass);
    }

    fields.forEach((item) => {
        item.removeAttribute('aria-invalid');

        if (error && error.id) {
            syncAriaDescribedBy(item, error.id, false);
        }
    });

    if (error) {
        error.remove();
    }
};

const showFieldValidation = (field, message, options) => {
    const wrapper = getFieldWrapper(field, options);
    const fields = field.form ? getGroupFields(field.form, field) : [field];
    const errorId = ensureErrorId(field);
    let error = getExistingError(wrapper, options) || getAnyError(wrapper, options);
    const shouldUpdateMessage = !error || error.dataset.formValidationError === 'true';

    if (error && !error.id) {
        error.id = errorId;
    }

    if (wrapper) {
        wrapper.classList.add(options.errorClass);
    }

    fields.forEach((item) => {
        item.setAttribute('aria-invalid', 'true');
        syncAriaDescribedBy(item, errorId, true);
    });

    if (!error) {
        error = document.createElement('span');
        error.className = options.errorMessageClass;
        error.dataset.formValidationError = 'true';
        error.setAttribute('role', 'alert');
        error.id = errorId;

        if (wrapper) {
            wrapper.appendChild(error);
        }
    }

    if (shouldUpdateMessage) {
        error.textContent = message;
    }
};

export const validateField = (field, options = {}) => {
    const config = mergeOptions(options);
    const form = field.form;
    const wrapper = getFieldWrapper(field, config);

    if (!form || !isElementVisible(field) || !isFieldRequired(field, config)) {
        clearFieldValidation(field, config);
        return true;
    }

    if (hasFieldValue(field, form)) {
        clearFieldValidation(field, config);
        return true;
    }

    showFieldValidation(field, getRequiredMessage(field, config, wrapper), config);

    return false;
};

const getRequiredFields = (form, options) => Array.from(form.querySelectorAll(options.fieldSelector))
    .filter((field) => isElementVisible(field) && isFieldRequired(field, options))
    .filter((field, index, fields) => {
        if (field.type !== 'radio' && field.type !== 'checkbox') {
            return true;
        }

        return fields.findIndex((item) => item.name === field.name) === index;
    });

export const validateForm = (form, options = {}) => {
    const config = mergeOptions(options);
    const fields = getRequiredFields(form, config);
    let firstInvalidField = null;

    fields.forEach((field) => {
        const isValid = validateField(field, config);

        if (!isValid && !firstInvalidField) {
            firstInvalidField = field;
        }
    });

    if (firstInvalidField && config.focusInvalid) {
        try {
            firstInvalidField.focus({ preventScroll: true });
        } catch (error) {
            firstInvalidField.focus();
        }

        try {
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (error) {
            firstInvalidField.scrollIntoView();
        }
    }

    return !firstInvalidField;
};

const bindFieldEvents = (form, field, options) => {
    const validate = () => validateField(field, options);
    const clearWhenFilled = () => {
        if (hasFieldValue(field, form)) {
            clearFieldValidation(field, options);
        }
    };

    if (field.type === 'radio' || field.type === 'checkbox' || field.tagName === 'SELECT') {
        field.addEventListener('change', validate);
        return;
    }

    field.addEventListener('blur', validate);
    field.addEventListener('input', clearWhenFilled);
};

export const initFormValidation = (formSelector = '[data-validate-form]', options = {}) => {
    const config = mergeOptions(options);
    const forms = typeof formSelector === 'string'
        ? Array.from(document.querySelectorAll(formSelector))
        : [formSelector].filter(Boolean);

    forms.forEach((form) => {
        if (form.dataset.formValidationReady === 'true') {
            return;
        }

        form.dataset.formValidationReady = 'true';

        Array.from(form.querySelectorAll(config.fieldSelector)).forEach((field) => {
            bindFieldEvents(form, field, config);
        });

        form.addEventListener('submit', (event) => {
            if (!validateForm(form, config)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        });
    });
};

export default {
    init: initFormValidation,
    validateField,
    validateForm,
    clearFieldValidation
};
